import apiClient from './apiClient';

export enum EmailVerificationProvider {
  REOON = 'reoon',
  BOUNCER = 'bouncer',
}

export interface EmailVerificationEstimateRequest {
  person_email_ids?: number[];
  place_email_ids?: number[];
  email_ids?: number[];
  email_type: 'person' | 'place' | 'both';
  bulk?: boolean;
  search_id?: number;
  // Filter fields (flattened, not nested)
  name?: string;
  min_rating?: number;
  max_rating?: number;
  min_reviews?: number;
  max_reviews?: number;
  has_website?: boolean;
  has_phone?: boolean;
  has_book_visit?: boolean;
  has_decision_makers?: boolean;
  has_email?: boolean;
  email_verification_status?: string;
  industries?: string;
  delivery_group?: 'website_confirmed' | 'additional';
  matched_offering?: string;
  google_ads_signal?: 'detected_recently' | 'no_ad_tracking';
  sort_by?: string;
  sort_order?: string;
}

interface ConfidenceLevelEstimate {
  count: number;
  cost_per: number;
  total: number;
  credits: number;
  emails?: Array<{ id: number; email: string }>;
}

export interface VerifyPlaceEstimate {
  place_id: number;
  place_name: string;
  email_count: number;
  person_email_ids: number[];
  place_email_ids: number[];
}

export interface EmailVerificationEstimateResponse {
  high_confidence: ConfidenceLevelEstimate;
  medium_confidence: ConfidenceLevelEstimate;
  low_confidence: ConfidenceLevelEstimate;
  generic_skipped: {
    count: number;
    emails?: Array<{ id: number; email: string }>;
  };
  catch_all_skipped?: {
    count: number;
    emails?: Array<{ id: number; email: string }>;
  };
  user_verified_emails: {
    count: number;
    emails?: Array<{ id: number; email: string; status: string }>;
  };
  total_hold: number;
  message: string;
  places: VerifyPlaceEstimate[];
}

export interface EmailVerificationRequest {
  person_email_ids?: number[];
  place_email_ids?: number[];
  email_ids?: number[];
  email_type: 'person' | 'place' | 'both';
  confidence_levels?: ('HIGH' | 'MEDIUM' | 'LOW')[];
  bulk?: boolean;
  search_id?: number;
  // Filter fields (flattened, not nested)
  name?: string;
  min_rating?: number;
  max_rating?: number;
  min_reviews?: number;
  max_reviews?: number;
  has_website?: boolean;
  has_phone?: boolean;
  has_book_visit?: boolean;
  has_decision_makers?: boolean;
  has_email?: boolean;
  email_verification_status?: string;
  industries?: string;
  delivery_group?: 'website_confirmed' | 'additional';
  matched_offering?: string;
  google_ads_signal?: 'detected_recently' | 'no_ad_tracking';
  sort_by?: string;
  sort_order?: string;
}

export interface EmailVerificationSyncResponse {
  request_id: number;
  status: string;
  results: Array<{
    email_id: number;
    email_table?: 'person' | 'place';
    email: string;
    status: string;
    charged: number;
    reason?: string;
  }>;
  credits_charged: number;
  credits_refunded: number;
  summary: string;
  processing_mode: 'sync';
}

export interface EmailVerificationAsyncResponse {
  request_id: number;
  credits_held: number;
  emails_processing: number;
  status_url: string;
  processing_mode: 'async';
}

export type EmailVerificationResponse = EmailVerificationSyncResponse | EmailVerificationAsyncResponse;


export interface VerificationStatusResponse {
  status: string;
  results: Array<{
    email_id: number;
    email_table?: 'person' | 'place';
    email: string;
    status: string;
    charged: number;
    reason?: string;
  }>;
  credits_charged: number;
  credits_refunded: number;
  summary: string;
  processed?: number;
  total?: number;
  by_status?: {
    GUARANTEED?: number;
    RISKY?: number;
    INVALID?: number;
    PENDING?: number;
    INBOX_FULL?: number;
  };
  verification_provider?: EmailVerificationProvider;
}

export interface ActiveVerificationResponse {
  has_active: boolean;
  request_id?: number;
  search_id?: number;
  total?: number;
  processed?: number;
  status?: string;
  credits_held?: number;
  started_at?: string;
  verification_provider?: EmailVerificationProvider;
}

export const emailVerificationApi = {
  /**
   * Estimate verification cost before committing
   */
  estimate: async (request: EmailVerificationEstimateRequest): Promise<EmailVerificationEstimateResponse> => {
    const response = await apiClient.post<EmailVerificationEstimateResponse>(
      '/v1/email-verification/estimate',
      request
    );
    return response.data;
  },

  /**
   * Start email verification process
   */
  verify: async (request: EmailVerificationRequest): Promise<EmailVerificationResponse> => {
    const response = await apiClient.post<EmailVerificationResponse>(
      '/v1/email-verification/verify',
      request
    );
    return response.data;
  },

  /**
   * Get verification status and results
   */
  getStatus: async (requestId: number): Promise<VerificationStatusResponse> => {
    const response = await apiClient.get<VerificationStatusResponse>(
      `/v1/email-verification/status/${requestId}`
    );
    return response.data;
  },

  /**
   * Check for active (in-progress) verification request
   * Used to restore state after page refresh
   */
  getActive: async (): Promise<ActiveVerificationResponse> => {
    const response = await apiClient.get<ActiveVerificationResponse>(
      '/v1/email-verification/active'
    );
    return response.data;
  },

  cancel: async (requestId: number): Promise<{ status: string; unprocessed_refunded?: number }> => {
    const response = await apiClient.delete(`/v1/email-verification/${requestId}`);
    return response.data;
  },
};
